import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useChildSessionStore } from "@/lib/stores/childSessionStore";
import { Button } from "@/components/ui/button.tsx";
import { FormattedMessage } from "react-intl";
import MediaPlayer from "@/components/ui/media-player";
import { treeOfLifeContent } from "@/lib/data/tree-of-life";
import { useState, useEffect } from "react";
import TreeOfLifeFlow from "@/components/Exercises/Tree-of-life/TreeOfLifeFlow";
import TreeOfLifeHistory from "@/components/Exercises/Tree-of-life/TreeOfLifeHistory";
import { useAuthStore } from "@/lib/stores/authStore";
import { useTreeOfLifeStore } from "@/lib/stores/treeOlLifeStore";
import { z } from "zod";
import { ExerciseLayout } from "@/components/ui/exercise-layout";

const treeOfLifeSearchSchema = z.object({
  view: z.enum(["prompt", "tree", "summary"]).optional(),
});

export const Route = createFileRoute(
  "/(content)/children/exercises/(shared)/tree-of-life/"
)({
  component: RouteComponent,
  validateSearch: (search) => treeOfLifeSearchSchema.parse(search),
  head: () => ({
    meta: [
      {
        title: "Tree of Life Exercise - HAMD",
      },
    ],
  }),
  beforeLoad: () => ({
    title: "treeOfLife.title",
    navbar: {
      showTitle: true,
    },
  }),
});

function RouteComponent() {
  const { view } = Route.useSearch();
  const [showExercise, setShowExercise] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<string | undefined>(
    undefined
  );
  const { user } = useAuthStore();
  const { hasSessions, fetchSessions } = useTreeOfLifeStore();
  const { selectedEmotionId } = useChildSessionStore();
  const navigate = useNavigate();

  const currentSegment = treeOfLifeContent[0];

  useEffect(() => {
    if (user) {
      fetchSessions();
    }
  }, [user, fetchSessions]);

  useEffect(() => {
    if (view === "summary" || view === "tree") {
      setShowExercise(true);
    }
  }, [view]);

  const handleEditSession = (sessionId: string) => {
    setEditingSessionId(sessionId);
    setShowHistory(false);
    setShowExercise(true);
  };

  const handleBackToHistory = () => {
    setEditingSessionId(undefined);
    setShowExercise(false);
    setShowHistory(true);
  };

  if (showExercise) {
    return (
      <TreeOfLifeFlow
        editingSessionId={editingSessionId}
        onBackToHistory={editingSessionId ? handleBackToHistory : undefined}
        initialView={view}
        onComplete={selectedEmotionId ? () => navigate({ to: "/children/session" }) : undefined}
      />
    );
  }
  if (showHistory) {
    return (
      <TreeOfLifeHistory
        onEditSession={handleEditSession}
        onBack={() => setShowHistory(false)}
      />
    );
  }
  return (
    <ExerciseLayout
      contentClassName="py-7.5 space-y-18"
      actions={
        <>
          <Button className="w-full" onClick={() => setShowExercise(true)}>
            <FormattedMessage id="treeOfLife.intro.start.button" />
          </Button>

          {user && hasSessions() && (
            <Button
              className="w-full"
              variant="secondary"
              onClick={() => setShowHistory(true)}
            >
              <FormattedMessage id="treeOfLife.intro.history.button" />
            </Button>
          )}
        </>
      }
    >
      <p className="text-small">
        <FormattedMessage id="treeOfLife.intro.description" />
      </p>

      <MediaPlayer
        src={currentSegment.audio}
        segment={currentSegment.segment}
      />
    </ExerciseLayout>
  );
}
